import { useRef, useState, useEffect } from "react";
import { FiArrowRight, FiMail, FiShield } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useProfile from "api/UserResource/useProfile";
import useSendEmailChangeOtp from "api/UserResource/useSendEmailChangeOtp";
import useChangeEmail from "api/UserResource/useChangeEmail";
import AccountField from "../AccountField";

const ChangeEmailAddress = () => {
	const { data: profile } = useProfile();
	const [verifying, setVerifying] = useState(false);
	const [feedback, setFeedback] = useState("");
	const verification = useRef(null);
	const { mutateAsync: sendOtp, isLoading: sending } = useSendEmailChangeOtp();
	const { mutateAsync: changeEmail, isLoading: changing } = useChangeEmail();
	const busy = sending || changing;
	useEffect(() => {
		if (verifying) verification.current?.querySelector("input")?.focus();
	}, [verifying]);
	return (
		<>
			<div className="account-email-current">
				<FiMail aria-hidden="true" />
				<div>
					<span className="account-eyebrow">Current email</span>
					<p>{profile?.data?.email}</p>
				</div>
				<span className="account-badge">Used to sign in</span>
			</div>
			<div className="account-stepper" aria-label="Email change progress">
				<span
					className={!verifying ? "is-active" : ""}
					aria-current={!verifying ? "step" : undefined}
				>
					<b>1</b> New email
				</span>
				<span
					className={verifying ? "is-active" : ""}
					aria-current={verifying ? "step" : undefined}
				>
					<b>2</b> Verify change
				</span>
			</div>
			<Formik
				initialValues={{ email: "", otp: "" }}
				validationSchema={Yup.object().shape({
					email: Yup.string()
						.required("Enter your new email address")
						.email("Enter a valid email address"),
					otp: verifying ? Yup.string().required("Enter the verification code") : Yup.string(),
				})}
				onSubmit={async (values, { resetForm }) => {
					try {
						if (!verifying) {
							await sendOtp({ email: values.email });
							setVerifying(true);
							setFeedback("A verification code has been sent to your current email.");
						} else {
							await changeEmail(values);
							setVerifying(false);
							resetForm();
							setFeedback("Your email address has been updated.");
						}
					} catch (_) {
						setFeedback("We couldn’t complete this step. Please try again.");
					}
				}}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<AccountField
							name="email"
							type="email"
							label="New email address"
							autoComplete="email"
							required
							disabled={verifying || busy}
							placeholder="you@example.com"
							help="This will become your new sign-in email."
						/>
						{verifying ? (
							<div className="account-verification" ref={verification}>
								<div className="account-note">
									<FiShield aria-hidden="true" />
									<div>
										<strong>Check your current inbox</strong>Enter the code sent to{" "}
										{profile?.data?.email} to confirm the change to {values.email}.
									</div>
								</div>
								<AccountField
									name="otp"
									label="Verification code"
									autoComplete="one-time-code"
									required
								/>
								<button
									type="button"
									className="account-inline-link"
									disabled={busy}
									onClick={async () => {
										try {
											await sendOtp({ email: values.email });
											setFeedback("A new code has been sent to your current email.");
										} catch (_) {
											setFeedback("Couldn’t resend the code. Please try again.");
										}
									}}
								>
									Resend verification code
								</button>
							</div>
						) : (
							<div className="account-note mt-6">
								<FiShield aria-hidden="true" />
								<div>
									<strong>One quick security check</strong>We’ll send a verification code to your
									current email before making the change.
								</div>
							</div>
						)}
						<div className="account-form-actions">
							<p role="status" aria-live="polite">
								{feedback || "Your email stays private."}
							</p>
							<div className="account-form-buttons">
								{verifying && (
									<button
										type="button"
										className="account-button secondary"
										disabled={busy}
										onClick={() => {
											setVerifying(false);
											setFieldValue("otp", "");
											setFeedback("");
										}}
									>
										Back
									</button>
								)}
								<button type="submit" className="account-button" disabled={busy}>
									{busy ? (
										<AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" />
									) : (
										<FiArrowRight aria-hidden="true" />
									)}
									{busy
										? "Please wait…"
										: verifying
											? "Confirm email change"
											: "Send verification code"}
								</button>
							</div>
						</div>
					</Form>
				)}
			</Formik>
		</>
	);
};
export default ChangeEmailAddress;
